/**
 * An attachment, shown in a tab of its own.
 *
 * # Why this is a tab and not a lightbox
 *
 * Because it is a thing you keep open while you do something else -- reading
 * the message that came with it, replying to it, checking a second attachment
 * against the first. A modal over the reader can be exactly one of those at a
 * time and closes the moment you look away, which is the opposite of what an
 * attachment is for. The tab strip already exists and already holds one thing
 * per tab; an attachment is one thing.
 *
 * # Everything here is hostile input
 *
 * A PDF is a program. An image is a decoder's input. Both arrived from
 * somebody who chose them, so the viewer treats them the way `lib/html.ts`
 * treats a message body: the bytes are handed to a sandboxed frame with no
 * access to this document, and nothing in the app parses them.
 *
 * Images are the one case where that is free -- `<img>` decodes in the
 * browser's own image pipeline, which is the same one every page on the
 * internet uses, and a decoded image cannot reach back. PDFs are not: they
 * need a renderer, that renderer is JavaScript, and it runs where it can do no
 * harm. See {@link Pdf}.
 *
 * # Saving stays available whatever is on screen
 *
 * A preview that cannot be saved from is a dead end, and the format this
 * cannot preview is exactly the one somebody most needs on their disk. So the
 * button is on the bar in every state, including the failures.
 */

import { useEffect, useRef, useState } from "preact/hooks";

import { fileSize } from "../lib/format";
import * as jmap from "../lib/jmap";
import * as store from "../lib/store";
import * as tabs from "../lib/tabs";
import * as Icon from "./Icons";

/** What this viewer can show inline, and how. */
type Shape = "image" | "pdf" | "none";

/**
 * How a part is displayed, from its declared type.
 *
 * The sender's word is taken for the *shape* and never for the bytes: a
 * mislabelled file renders as a broken image or a failed parse inside the
 * sandbox, which is a bad preview rather than a foothold. Sniffing the content
 * instead would mean parsing hostile input out here to decide where to send it,
 * which is the thing this is arranged to avoid.
 */
export function shapeOf(type: string): Shape {
  const mime = type.toLowerCase().split(";")[0]?.trim() ?? "";
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  return "none";
}

export function AttachmentView({ address }: { address: tabs.Address }) {
  const part = address.part;
  const session = store.session.value;
  const [bytes, setBytes] = useState<Blob | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    if (!part || !session) return;
    let live = true;
    setBytes(null);
    setFailed(null);
    jmap
      .fetchAttachment(session, address.account, part)
      .then((blob) => {
        if (live) setBytes(blob);
      })
      .catch((e: unknown) => {
        if (live) setFailed(e instanceof Error ? e.message : String(e));
      });
    return () => {
      live = false;
    };
    // The session is a dependency, not just a guard. A tab restored at startup
    // renders before the session document has been fetched, so the first run
    // of this effect bails out above -- and with the session left out of this
    // list, nothing ran it again once there was one. The tab sat on the
    // spinner for the life of the window: the "PDF opens as a white page" of
    // the desktop app, seen only on a restart with an attachment tab open. A
    // message tab never had this, because the reader reads the session signal
    // while rendering and is redrawn when it changes.
  }, [session, address.account, address.email, part?.blobId]);

  if (!part) return null;

  return (
    <section class="col reader-col" data-pane-name="reader" tabIndex={-1} aria-label={part.name}>
      <div class="reader">
        <div class="reader-head">
          <div class="reader-title">
            <button
              class="icon-btn back-btn"
              aria-label="Back to the message"
              onClick={() => tabs.close(tabs.active.value)}
            >
              <Icon.Back />
            </button>
            <h1 class="reader-subject">{part.name}</h1>
          </div>
          <div class="att-bar">
            <span class="addr">
              {part.type || "unknown type"}
              {bytes ? ` · ${fileSize(bytes.size)}` : ""}
            </span>
            <span class="spacer" />
            {/*
              On the bar rather than only in the empty state, because the
              format this cannot preview is the one most likely to be wanted on
              disk -- and because somebody looking at a preview is often
              deciding whether to keep it.
            */}
            <button
              class="btn"
              disabled={!bytes}
              onClick={() => {
                if (!session) return;
                void jmap
                  .saveAttachment(session, address.account, part)
                  .catch((e: unknown) =>
                    store.toast(e instanceof Error ? e.message : String(e), "error"),
                  );
              }}
            >
              <Icon.KeepOffline />
              <span>Save to device…</span>
            </button>
          </div>
        </div>

        <Body shape={shapeOf(part.type)} name={part.name} bytes={bytes} failed={failed} />
      </div>
    </section>
  );
}

function Body({
  shape,
  name,
  bytes,
  failed,
}: {
  shape: Shape;
  name: string;
  bytes: Blob | null;
  failed: string | null;
}) {
  if (failed) {
    return (
      <div class="empty">
        <Icon.Warning />
        <h2>That attachment could not be fetched</h2>
        <p>{failed}</p>
      </div>
    );
  }
  if (!bytes) {
    return (
      <div class="empty" aria-busy="true">
        <span class="spinner" />
      </div>
    );
  }
  if (shape === "image") return <Image name={name} bytes={bytes} />;
  if (shape === "pdf") return <Pdf name={name} bytes={bytes} />;
  return (
    <div class="empty">
      <Icon.Paperclip />
      <h2>No preview for this kind of file</h2>
      <p>Save it to open it in something that understands it.</p>
    </div>
  );
}

/**
 * An image, decoded by the browser rather than by anything in this app.
 *
 * The object URL is revoked when the tab goes, which matters more here than it
 * usually does: an attachment can be tens of megabytes and a tab is closed far
 * more often than the app is.
 */
function Image({ name, bytes }: { name: string; bytes: Blob }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const made = URL.createObjectURL(bytes);
    setUrl(made);
    return () => URL.revokeObjectURL(made);
  }, [bytes]);

  if (!url) return <div class="empty" aria-busy="true" />;
  return (
    <div class="att-canvas">
      <img class="att-image" src={url} alt={name} />
    </div>
  );
}

/**
 * A PDF, rendered by pdf.js inside a sandbox.
 *
 * # The sandbox is the whole design
 *
 * A PDF is a program, and this one came from a stranger. The renderer is a
 * megabyte of JavaScript whose job is to parse it, so the question is not
 * whether pdf.js has bugs -- it is what a bug can reach. It runs in a frame
 * with `allow-scripts` and deliberately **without** `allow-same-origin`, which
 * is the pair that produces an opaque origin: script may run, and it cannot
 * touch this document, its `localStorage`, or the device token in it. Nothing
 * out here parses a byte of the file.
 *
 * That is the same bargain `lib/html.ts` strikes for message bodies, with one
 * difference worth stating: mail bodies are given no script at all, and this
 * frame must have it. So the isolation is doing more work here, and the CSP
 * inside the document is what stops the renderer reaching the network with
 * whatever it read.
 *
 * # The frame is a real file, and it has to be
 *
 * `public/attachment-viewer.html`, served from the app's own origin. The
 * first version assembled the document at runtime and handed it over as
 * `srcdoc`, and that shipped a viewer that was blank in the packaged desktop
 * app: a srcdoc document -- like blob: and data: documents -- inherits the
 * embedder's Content-Security-Policy, the strict one from `tauri.conf.json`
 * forbids inline script, and the code that would have reported the failure
 * was the code being blocked. The dev server injects no CSP, so development
 * never saw it. A document loaded from a URL is the one kind that carries
 * its own policy; the sandbox attribute keeps its origin opaque all the same.
 *
 * The viewer's script is *inline* in that file and admitted *by hash* in
 * `tauri.conf.json` -- the comment atop the file explains why nothing
 * origin-based can work inside the sandbox, and `dev/parity.mjs` keeps the
 * hash honest.
 *
 * # Why the renderer arrives as strings, by postMessage
 *
 * The frame cannot *fetch* pdf.js: a module script is subject to CORS, and a
 * null origin asking this app's own server for one is a cross-origin request
 * needing headers the daemon does not send. So the parent -- which can read
 * its own assets -- loads the sources as strings and posts them over; the
 * frame turns them into blob URLs and imports those, which stays inside its
 * own origin. Verified in WebKitGTK: an opaque origin may create blob URLs,
 * import modules from them, and construct module workers from them.
 *
 * The sources are ~2.7 MB, so they are loaded on demand -- `import()` rather
 * than a static import, so the browser build, which has no tabs and never
 * renders this, never downloads them -- and kept for every PDF opened after.
 * The attachment bytes ride in the same message, transferred rather than
 * copied. The frame asks when it is ready and the parent answers: `load` can
 * fire before the script inside has run, and a message posted then is simply
 * lost, so waiting to be asked is the only ordering that cannot race.
 */
function Pdf({ name, bytes }: { name: string; bytes: Blob }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    // The frame announces itself until answered (see the script in
    // `public/attachment-viewer.html` for the race that made that necessary),
    // so the answer goes once: the buffer is transferred, and a second send
    // would be a second copy for a frame that ignores it.
    let answered = false;
    const onMessage = async (e: MessageEvent) => {
      if (e.source !== frame.current?.contentWindow) return;
      if ((e.data as { hm?: string })?.hm !== "ready" || answered) return;
      answered = true;
      try {
        const [{ lib, worker }, buffer] = await Promise.all([
          viewerSources(),
          bytes.arrayBuffer(),
        ]);
        // `*` because the frame has an opaque origin and can never match a
        // target: there is no origin to name. The message carries no secret --
        // pdf.js is public source and the buffer is the attachment the frame
        // was opened to show.
        frame.current?.contentWindow?.postMessage({ hm: "pdf", lib, worker, buffer }, "*", [
          buffer,
        ]);
      } catch (err) {
        setFailed(err instanceof Error ? err.message : String(err));
      }
    };
    window.addEventListener("message", onMessage);
    // A frame that never announces itself would otherwise be a grey page with
    // no explanation, which is exactly the report this guards the next time.
    // Generous, because a cold start on a slow disk really can take a while.
    const silent = setTimeout(() => {
      if (!answered) setFailed("The viewer frame did not start.");
    }, 15_000);
    return () => {
      clearTimeout(silent);
      window.removeEventListener("message", onMessage);
    };
  }, [bytes]);

  if (failed) {
    return (
      <div class="empty">
        <Icon.Warning />
        <h2>The PDF viewer could not be loaded</h2>
        <p>{failed}</p>
      </div>
    );
  }

  return (
    <iframe
      ref={frame}
      class="att-frame"
      title={name}
      sandbox="allow-scripts"
      referrerpolicy="no-referrer"
      src="/attachment-viewer.html"
    />
  );
}

/** The pdf.js sources, loaded once and kept. */
let sources: Promise<{ lib: string; worker: string }> | null = null;

function viewerSources(): Promise<{ lib: string; worker: string }> {
  // `?raw` so these arrive as source to hand to the frame rather than as
  // modules to run out here -- nothing in this document ever executes pdf.js.
  sources ??= Promise.all([
    import("pdfjs-dist/build/pdf.min.mjs?raw"),
    import("pdfjs-dist/build/pdf.worker.min.mjs?raw"),
  ]).then(([lib, worker]) => ({ lib: lib.default, worker: worker.default }));
  return sources;
}
